// The class strings the admin's components share, on the design system's
// tokens (docs/design-system.md in poweryourflow-ops).

/** The tracked 11 px label, set in capitals. */
export const label = "text-label font-semibold tracking-[var(--ls-label)] uppercase";

/** A ring in the focus color when the keyboard reaches a control. */
export const focusRing = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet";

/**
 * SPEC-47: a control drawn smaller than a finger, a segment or a text link,
 * still takes a tap 44 px tall (--touch-min), centred on it. The extra
 * height is an empty ::before, so nothing moves on the page.
 */
export const hitArea =
  "relative before:absolute before:inset-x-0 before:top-1/2 before:h-[var(--touch-min)] before:-translate-y-1/2";

/**
 * SPEC-47: a screen of the admin, on cream, its side margin growing with the
 * window from 16 to 40 px. index.html draws under a phone's notch
 * (viewport-fit=cover), so a phone on its side keeps at least the notch's
 * inset.
 */
export const pageFrame =
  "min-h-dvh bg-cream pr-[max(var(--spacing-page),env(safe-area-inset-right))] pl-[max(var(--spacing-page),env(safe-area-inset-left))]";

/** SPEC-47: a screen's title, from 28 px on a phone to 40 px on a desktop. */
export const pageTitle = "text-page-title leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)] text-balance";

/**
 * SPEC-47: the four counters, in a row from a tablet up. On a phone, two a
 * row, each at least 150 px: a track is half the row less half the gap
 * (and a pixel, so rounding never drops it to one), or 150 px when half is
 * less, and then only one fits a row.
 */
export const statGrid =
  "grid grid-cols-[repeat(auto-fit,minmax(max(150px,calc(50%_-_var(--spacing)*2_-_1px)),1fr))] gap-4 tablet:grid-cols-4";

/**
 * SPEC-47: an athlete's header, loaded or as a skeleton. Below a desktop, a
 * grid: her avatar beside her name, her status and the export under the
 * name (col-start-2), the avatar and the name joining the grid through
 * profileHeadPerson's contents. On a desktop, the row it was before.
 */
export const profileHead =
  "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 tablet:gap-x-5 desk:flex desk:flex-wrap desk:justify-between desk:gap-6";
export const profileHeadPerson = "contents desk:flex desk:min-w-0 desk:items-center desk:gap-5";

/**
 * SPEC-47: Dados gerais and her assessments, 3 to 5 as before, side by side
 * while each has 320 px: a column grows from nothing but never below 320 px,
 * so the pair wraps, Dados gerais first, once the row cannot hold both.
 * min() keeps a stacked column inside a screen narrower than 320 px.
 */
export const profileColumns = "flex flex-wrap items-start gap-8 pt-4 desk:gap-x-14";
const profileColumn = "flex min-w-[min(20rem,100%)] flex-col gap-4";
export const generalColumn = `${profileColumn} flex-[3_1_0%]`;
export const assessmentsColumn = `${profileColumn} flex-[5_1_0%]`;

/** SPEC-47: an assessment row's middle. Its title keeps 160 px; past that, the score and the tag go under it rather than squeeze it. */
export const rowMiddle = "flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2";
export const rowTitle = "flex min-w-0 grow basis-40 flex-col";

/** The first link of a page, shown when the keyboard reaches it: straight to the main content. */
export const skipLink =
  "sr-only rounded-pill bg-white px-4 py-2 text-body-sm font-semibold focus:not-sr-only focus:absolute focus:top-2 focus:left-2 " + focusRing;

/** A link in the page's text, in Dark: « ← Base de utilizadoras ». */
export const textLink = `${hitArea} rounded-pill text-body-sm font-semibold text-dark no-underline hover:text-dark hover:underline ${focusRing}`;

/** No double-tap zoom on a control, so a tap answers at once. */
const touch = "touch-manipulation";

const pill =
  "flex h-[var(--control-h-sm)] shrink-0 items-center justify-center rounded-pill px-6 text-body-sm font-semibold whitespace-nowrap " +
  `transition-colors duration-[var(--dur-micro)] ease-pyf ${touch} ${focusRing}`;

/** The screen's one main action, Dark. */
export const primaryButton = `${pill} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`;

/** An outlined pill: it fills cream on hover. */
export const outlineButton = `${pill} border-[1.5px] border-line-20 bg-white text-dark hover:bg-cream-deep disabled:bg-white disabled:text-disabled`;

/**
 * SPEC-56: a round button with one glyph, 36 px drawn and outlined. Its tap
 * reaches 4 px past it on every side, 44 px in all (--touch-min), through
 * an empty ::before, so nothing moves on the page. The glyph is the
 * caller's, hidden from a screen reader; its words are the aria-label.
 */
export const roundButton =
  "relative flex size-9 shrink-0 items-center justify-center rounded-pill border-[1.5px] border-line-20 bg-white text-body-lg leading-none text-dark " +
  "before:absolute before:-inset-1 transition-colors duration-[var(--dur-micro)] ease-pyf enabled:hover:bg-cream-deep " +
  `disabled:cursor-default disabled:border-line-10 disabled:text-disabled ${touch} ${focusRing}`;

/** The track of a segmented control, and a segment on it, current or not. */
export const segmentTrack = "flex items-center rounded-pill bg-cream-deep p-1";
const segment =
  "flex h-9 items-center rounded-pill px-4 text-body-sm whitespace-nowrap " +
  `transition-colors duration-[var(--dur-micro)] ease-pyf ${touch} ${hitArea} ${focusRing}`;
export const segmentOn = `${segment} bg-white font-semibold text-dark shadow-segment`;
export const segmentOff = `${segment} font-medium text-muted hover:text-dark`;
export const segmentDisabled = `${segment} cursor-not-allowed font-medium text-disabled`;
